import { Checkbox, FormControlLabel } from '@mui/material';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { useCreateIntegratorMutation } from '@/api/v1';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { VerifyBusinessHeader } from '@/components/organisms/VerifyBusinessHeader';
import { VerifyBusinessSummaryMenu } from '@/components/organisms/VerifyBusinessSummaryMenu';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { useVerifyBusinessSteps } from '@/hooks/useVerifyBusinessSteps';
import { computeValidRoutePath } from '@/utility/computeValidRoutePath';

type StepParameters = {
  readonly parentId: string;
  readonly childId: string;
};

export const VerifyBusinessSummary: React.FC = (): React.ReactElement => {
  const { parentId, childId } = useParams<StepParameters>();

  const [acceptedDeclaration, setAcceptedDeclaration] = React.useState<boolean>(false);
  const [createIntegrator, createIntegratorMutation] = useCreateIntegratorMutation();

  const steps = useVerifyBusinessSteps();
  const navigate = useNavigate();
  const tr = useTranslator();
  const toast = useToast();

  const onSubmit = React.useCallback((): void => {
    createIntegrator();
  }, [createIntegrator]);

  const validateRouterPath = React.useCallback((): void => {
    const validRoutePath = computeValidRoutePath(steps, parentId, childId);
    if (validRoutePath) {
      navigate(validRoutePath);
    }
  }, [childId, navigate, parentId, steps]);

  React.useLayoutEffect((): void => {
    validateRouterPath();
  }, [validateRouterPath]);

  React.useEffect((): void => {
    switch (createIntegratorMutation.status) {
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      case QueryStatus.fulfilled:
        toast.show(tr('CREATE_INTEGRATOR_SUCCESS'), 'success');
        navigate('/home');
        break;
      default:
    }
  }, [createIntegratorMutation.status, navigate, toast, tr]);

  const onDeclarationAcceptanceChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { target: input } = event;
      setAcceptedDeclaration(input.checked);
    },
    [],
  );

  const canSubmit = React.useMemo((): boolean => acceptedDeclaration, [acceptedDeclaration]);

  return (
    <Box sx={styles.container}>
      <VerifyBusinessHeader />

      <Box sx={styles.title}>
        <Typography variant="h5">{tr('REVIEW_AND_SUBMIT')}</Typography>
      </Box>

      <Paper sx={styles.leftPanel} elevation={0}>
        <VerifyBusinessSummaryMenu />
      </Paper>

      <Box sx={styles.stepContainer}>
        <Paper sx={styles.stepContainerPaper} elevation={0}>
          <Outlet />
        </Paper>
      </Box>

      <Box sx={styles.footer}>
        <FormControlLabel
          sx={styles.checkbox}
          control={
            <Checkbox checked={acceptedDeclaration} onChange={onDeclarationAcceptanceChange} />
          }
          label={
            <Typography variant="body2" color="text.primary">
              {tr('I_AGREE_TO_THE')}
              <Link href="">{tr('CALIZA_TERMS_AND_SERVICES')}</Link>
            </Typography>
          }
        />
        <LoadingButton
          sx={styles.submitButton}
          variant="contained"
          loading={createIntegratorMutation.status === QueryStatus.pending}
          disabled={!canSubmit}
          onClick={onSubmit}>
          {tr('SUBMIT')}
        </LoadingButton>
      </Box>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    position: 'relative',
    display: 'grid',
    gridTemplateRows: '104px auto 698px auto',
    gridTemplateColumns: '1fr 290px 814px 1fr',
    rowGap: 0,
    columnGap: 3,
    height: '100%',
    width: '100%',
    overflowY: 'scroll',
    backgroundColor: 'custom.InputsBackground',
  },
  stepContainerPaper: {
    height: '100%',
  },
  checkbox: {
    width: 808,
    gap: 1,
  },
  submitButton: {
    width: 104,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gridRow: 4,
    gridColumn: '2 / span 2',
    py: '28px',
    my: 3,
  },
  title: {
    pt: 7,
    pb: 5,
    gridColumn: '2 / span 3',
  },
  leftPanel: {
    width: '100%',
    height: '100%',
    gridColumn: '2',
  },
  stepContainer: {
    gridColumn: '3 / span 1',
    minHeight: 698,
    width: '100%',
  },
};
